import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { 
  CalendarIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  UserGroupIcon,
  HomeIcon,
  ChatBubbleLeftEllipsisIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';

const ResidentDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myInvoices, setMyInvoices] = useState<any[]>([]);
  const [myIssues, setMyIssues] = useState<any[]>([]);
  const [communityEvents, setCommunityEvents] = useState<any[]>([]);
  const [vendorCount, setVendorCount] = useState<number>(0);
  const [propertyInfo, setPropertyInfo] = useState<any>(null);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResidentData = async () => {
      if (!user?.propertyGroupId || !user?.id) return;
      
      try {
        // Fetch resident-specific data
        const pgId = user.propertyGroupId;

        const [invoicesRes, issuesRes, eventsRes, vendorsRes, propertyRes, announcementsRes] = await Promise.all([
          api.get('/api/invoices', { params: { hoaId: pgId } }),
          api.get('/api/issues'),
          api.get('/api/events', { params: { hoaId: pgId } }),
          api.get('/api/vendors').catch(() => ({ data: [] })),
          api.get(`/api/hoas/${pgId}`).catch(() => ({ data: null })),
          api.get('/api/announcements', { params: { hoaId: pgId } }).catch(() => ({ data: [] })),
        ]);
        
        setMyInvoices((invoicesRes.data as any[]).filter((i: any) => i.status !== 'PAID').slice(0, 5));
        setMyIssues((issuesRes.data as any[]).filter((i: any) => i.status === 'open').slice(0, 3));
        setCommunityEvents((eventsRes.data as any[]).slice(0, 5));
        setVendorCount((vendorsRes.data as any[]).length || 0);
        setPropertyInfo(propertyRes.data);
        setAnnouncements((announcementsRes.data as any[]).slice(0, 3));
      } catch (error) {
        console.error('Error fetching resident data:', error);
        // Set empty arrays if API calls fail
        setMyInvoices([]);
        setMyIssues([]);
        setCommunityEvents([]);
        setVendorCount(0);
      }
    };
    
    fetchResidentData();
  }, [user?.propertyGroupId, user?.id, user?.token]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handlePayInvoice = async (invoiceId: string) => {
    setPayingInvoiceId(invoiceId);
    setPayError(null);
    try {
      const res = await api.post<{ url: string }>('/api/stripe/create-invoice-payment', { invoiceId });
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Payment failed. Please try again.';
      setPayError(msg);
    } finally {
      setPayingInvoiceId(null);
    }
  };

  // Calculate outstanding balance from pending invoices
  const outstandingBalance = myInvoices.reduce((total, invoice) => {
    const amount = typeof invoice.amount === 'number' ? invoice.amount : parseFloat(invoice.amount || 0);
    return total + amount;
  }, 0);
  
  // Resident-specific stats
  const residentStats = [
    { 
      label: 'My Outstanding Balance', 
      value: outstandingBalance > 0 ? `$${outstandingBalance.toFixed(2)}` : '$0.00', 
      icon: CreditCardIcon, 
      trend: outstandingBalance > 0 ? 'Due Soon' : 'Current', 
      clickable: true, 
      route: '/payments' 
    },
    { 
      label: 'My Open Requests', 
      value: myIssues.length.toString(), 
      icon: ExclamationTriangleIcon, 
      trend: myIssues.length > 0 ? 'In Progress' : 'None Open', 
      clickable: true, 
      route: '/issues' 
    },
    { 
      label: 'Community Events', 
      value: communityEvents.length.toString(), 
      icon: CalendarIcon, 
      trend: communityEvents.length > 0 ? 'This Month' : 'None Scheduled', 
      clickable: true, 
      route: '/events' 
    },
    { 
      label: 'Available Services', 
      value: vendorCount.toString(), 
      icon: UserGroupIcon, 
      trend: vendorCount > 0 ? 'Verified Vendors' : 'None Available', 
      clickable: true, 
      route: '/vendors' 
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="floating-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#22d3ee] to-[#38bdf8] flex items-center justify-center">
              <HomeIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Welcome back, {user?.name}!</h1>
              <p className="text-white/60 mt-1">Here's what's happening in your community</p>
            </div>
          </div>
          {propertyInfo && (
            <div className="text-right">
              <p className="text-sm text-white/40">Your Property Group</p>
              <p className="text-lg font-semibold text-white">{propertyInfo.name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Resident Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {residentStats.map((stat, index) => (
          <div 
            key={stat.label} 
            className={`floating-card group ${stat.clickable ? 'cursor-pointer hover:scale-105' : ''}`}
            onClick={() => stat.clickable && stat.route && navigate(stat.route)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span className="text-xs font-medium text-[#22d3ee]">
                    {stat.trend}
                  </span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                <stat.icon className="w-6 h-6 text-[#22d3ee]" />
              </div>
            </div>
            {stat.clickable && (
              <div className="mt-2 text-xs text-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Click to view details →
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* My Payments */}
        <div className="floating-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500/20 to-blue-500/20 flex items-center justify-center mr-3">
              <CreditCardIcon className="w-5 h-5 text-green-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Outstanding Bills</h2>
          </div>

          {payError && (
            <div className="mb-4 px-3 py-2.5 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-2">
              <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-400 text-xs">{payError}</p>
            </div>
          )}
          
          <div className="space-y-3">
            {myInvoices.length > 0 ? (
              myInvoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-3 rounded-xl bg-white/3 hover:bg-white/5 transition-colors">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white text-sm truncate">
                      {invoice.description || 'Invoice'}
                    </h3>
                    <p className="text-xs text-white/50 mt-0.5">
                      ${(typeof invoice.amount === 'number' ? invoice.amount : parseFloat(invoice.amount || 0)).toFixed(2)} · Due {formatDate(invoice.dueDate)}
                    </p>
                  </div>
                  <button
                    onClick={() => handlePayInvoice(invoice.id)}
                    disabled={payingInvoiceId === invoice.id}
                    className="ml-3 flex-shrink-0 px-3 py-1.5 bg-[#22d3ee] hover:bg-[#6d4fd4] disabled:opacity-60 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                  >
                    {payingInvoiceId === invoice.id ? (
                      <>
                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Processing…
                      </>
                    ) : (
                      'Pay Now'
                    )}
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-white/60">
                <svg className="w-10 h-10 text-white/20 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-medium">All caught up!</p>
                <p className="text-sm mt-1">No outstanding bills</p>
              </div>
            )}
          </div>
          
          <button
            className="w-full mt-4 btn-secondary text-sm"
            onClick={() => navigate('/payments')}
          >
            View All Payments →
          </button>
        </div>

        {/* My Service Requests */}
        <div className="floating-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500/20 to-cyan-400/20 flex items-center justify-center mr-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">My Requests</h2>
          </div>
          
          <div className="space-y-4">
            {myIssues.length > 0 ? (
              myIssues.map((issue, index) => (
                <div key={issue.id} className="group">
                  <div className="flex items-start space-x-3 p-3 rounded-xl hover:bg-white/5 transition-colors duration-200">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-blue-400"></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white group-hover:text-blue-400 transition-colors">
                        {issue.title}
                      </h3>
                      <p className="text-sm text-white/60 mt-1">
                        Submitted: {formatDate(issue.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-white/60">
                <p>No open requests</p>
                <p className="text-sm mt-1">All issues resolved</p>
              </div>
            )}
          </div>
          
          <button 
            className="w-full mt-4 btn-secondary text-sm"
            onClick={() => navigate('/issues')}
          >
            Submit New Request
          </button>
        </div>

        {/* Community Updates */}
        <div className="floating-card">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center mr-3">
              <MegaphoneIcon className="w-5 h-5 text-[#22d3ee]" />
            </div>
            <h2 className="text-xl font-semibold text-white">Community Updates</h2>
          </div>

          <div className="space-y-3">
            {announcements.length > 0 ? (
              announcements.map((ann: any) => (
                <div key={ann.id} className="p-3 rounded-xl bg-white/5 hover:bg-white/8 transition-colors">
                  <p className="text-sm font-medium text-white">{ann.title}</p>
                  <p className="text-xs text-white/60 mt-1 line-clamp-2">{ann.content}</p>
                  <p className="text-xs text-white/30 mt-1">{formatDate(ann.createdAt)}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-white/60">
                No community updates available.
                <br />
                <span className="text-sm">Check back later for announcements.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upcoming Community Events */}
      <div className="floating-card">
        <div className="flex items-center mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#22d3ee]/20 to-[#38bdf8]/20 flex items-center justify-center mr-3">
            <CalendarIcon className="w-5 h-5 text-[#22d3ee]" />
          </div>
          <h2 className="text-xl font-semibold text-white">Upcoming Events</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {communityEvents.length > 0 ? (
            communityEvents.slice(0, 3).map((event, index) => (
              <div key={event.id} className="group p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors duration-200">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium text-white group-hover:text-[#22d3ee] transition-colors">
                    {event.title}
                  </h3>
                  <span className="text-xs text-white/60 bg-white/10 px-2 py-1 rounded-lg">
                    {formatDate(event.date)}
                  </span>
                </div>
                <p className="text-sm text-white/60 mb-3">{event.location}</p>
                <button className="w-full btn-secondary text-xs py-2">
                  RSVP
                </button>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-white/60">
              <p>No upcoming events scheduled</p>
              <p className="text-sm mt-1">Check back later for community events</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions for Residents */}
      <div className="floating-card">
        <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            className="btn-primary flex items-center justify-center space-x-2 py-4"
            onClick={() => navigate('/payments')}
          >
            <CreditCardIcon className="w-5 h-5" />
            <span>Pay Dues</span>
          </button>
          <button
            className="btn-secondary flex items-center justify-center space-x-2 py-4"
            onClick={() => navigate('/issues')}
          >
            <ExclamationTriangleIcon className="w-5 h-5" />
            <span>Report Issue</span>
          </button>
          <button
            className="btn-secondary flex items-center justify-center space-x-2 py-4"
            onClick={() => navigate('/vendors')}
          >
            <UserGroupIcon className="w-5 h-5" />
            <span>Find Vendors</span>
          </button>
          <button
            className="btn-secondary flex items-center justify-center space-x-2 py-4"
            onClick={() => navigate('/issues')}
          >
            <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
            <span>Contact Board</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResidentDashboard; 